/**
 * @file ShopDashboardTopSkus.tsx
 * @description «Топ-10 товаров за период» на «Сводке» (phase19.31, разбор A
 * макет (г)).
 *
 * Строка: место · товар и фасовка («Райский сад · 50 г», `skuLabel` с
 * сервера, SC49) · продано штук · выручка без копеек. На телефоне название
 * переносится, числа остаются справа.
 */

import { TrendingUp } from "lucide-react";
import type { ReactNode } from "react";

import { formatRubShort, periodLabel } from "./dashboard-model";
import { SectionCard } from "../ui/SectionCard";
import { Skeleton } from "../ui/skeleton";
import type { TopSkuDto } from "../../lib/api-orders";

interface Props {
  top: TopSkuDto[] | null;
  error: boolean;
  days: number;
}

/** «Райский сад · 50 г». */
function skuName(sku: TopSkuDto): string {
  return sku.skuLabel ? `${sku.productName} · ${sku.skuLabel}` : sku.productName;
}

export function ShopDashboardTopSkus({ top, error, days }: Props) {
  let body: ReactNode;
  if (error && !top) {
    body = <p className="type-body text-on-surface-variant">Не удалось загрузить топ товаров.</p>;
  } else if (!top) {
    body = (
      <div aria-busy="true" className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-6" />
        ))}
      </div>
    );
  } else if (top.length === 0) {
    body = (
      <p className="type-body flex items-center gap-2 text-on-surface-variant">
        <TrendingUp aria-hidden className="size-5 shrink-0" />
        Продаж за период нет
      </p>
    );
  } else {
    body = (
      <ol className="-mx-1 flex flex-col divide-y divide-[var(--border)]">
        {top.map((sku, index) => (
          <li key={`${sku.skuId ?? "none"}-${index}`} className="flex min-h-12 items-center gap-3 px-1 py-2">
            <span className="type-caption w-5 shrink-0 text-right tabular-nums text-on-surface-variant">{index + 1}</span>
            <span className="type-body min-w-0 flex-1 break-words text-on-surface">{skuName(sku)}</span>
            <span className="type-caption shrink-0 tabular-nums text-on-surface-variant">{sku.qtySold} шт</span>
            <span className="type-body w-24 shrink-0 text-right font-semibold tabular-nums text-on-surface">
              {formatRubShort(sku.revenue)}
            </span>
          </li>
        ))}
      </ol>
    );
  }

  return <SectionCard title={`Топ-10 товаров за ${periodLabel(days)}`}>{body}</SectionCard>;
}
